import { Button } from '@trellis/roots/button'
import { CsvDropZone, CurrencyInput, DatePicker } from '@trellis/roots/forms'
import { Input } from '@trellis/roots/input'
import { Label } from '@trellis/roots/label'
import { FormDialog } from '@trellis/roots/overlays'
import { Textarea } from '@trellis/roots/textarea'
import { Typography } from '@trellis/roots/typography'
import { FileText, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { useRecordPaymentMutation, useUploadPaymentReceiptMutation } from '@/hooks'
import { formatCurrency, nowTime, todayIsoDate } from '@/lib/format'

const ACCEPTED_RECEIPT_MIME = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']
const MAX_RECEIPT_SIZE_BYTES = 10 * 1024 * 1024

interface RecordPaymentDialogProps {
  invoiceId: string
  currency: string
  outstandingAmount: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RecordPaymentDialog({
  invoiceId,
  currency,
  outstandingAmount,
  open,
  onOpenChange,
}: RecordPaymentDialogProps) {
  const [amount, setAmount] = useState(outstandingAmount)
  const [paidAt, setPaidAt] = useState(todayIsoDate)
  const [time, setTime] = useState(nowTime)
  const [note, setNote] = useState('')
  const [file, setFile] = useState<File | null>(null)

  const recordPayment = useRecordPaymentMutation(invoiceId)
  const uploadReceipt = useUploadPaymentReceiptMutation(invoiceId)

  const isAmountValid = amount > 0
  const exceedsOutstanding = isAmountValid && amount > outstandingAmount
  const isTimeValid = time.length > 0
  const isSubmitting = recordPayment.isPending || uploadReceipt.isPending

  useEffect(() => {
    if (open) {
      setAmount(outstandingAmount)
      setPaidAt(todayIsoDate())
      setTime(nowTime())
      setNote('')
      setFile(null)
    }
    // Re-seed from the live outstandingAmount at the moment the dialog opens,
    // not whenever it changes while already open (that would clobber in-progress edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function handleFile(selectedFile: File) {
    if (!ACCEPTED_RECEIPT_MIME.includes(selectedFile.type)) {
      toast.error('Unsupported file type', {
        description: 'Use JPEG, PNG, WEBP, or PDF.',
      })
      return
    }
    if (selectedFile.size > MAX_RECEIPT_SIZE_BYTES) {
      toast.error('File too large', { description: 'Maximum size is 10 MB.' })
      return
    }
    setFile(selectedFile)
  }

  function handleSubmit() {
    if (!isAmountValid || exceedsOutstanding || !isTimeValid) return
    recordPayment.mutate(
      {
        amount,
        paidAt: new Date(`${paidAt}T${time}:00`).toISOString(),
        note: note.trim() || undefined,
      },
      {
        onSuccess: (result) => {
          const paymentId = result.data?.id
          if (file && paymentId) {
            uploadReceipt.mutate(
              { paymentId, file },
              {
                onSuccess: () => {
                  toast.success('Payment recorded')
                  onOpenChange(false)
                },
                onError: (caughtError) => {
                  toast.error('Payment recorded, but receipt upload failed', {
                    description:
                      caughtError instanceof Error
                        ? caughtError.message
                        : 'Please try again',
                  })
                  onOpenChange(false)
                },
              },
            )
            return
          }
          toast.success('Payment recorded')
          onOpenChange(false)
        },
        onError: (caughtError) => {
          toast.error('Failed to record payment', {
            description:
              caughtError instanceof Error ? caughtError.message : 'Please try again',
          })
        },
      },
    )
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      className="sm:max-w-[480px]"
      title="Record Payment"
      submitLabel="Record Payment"
      pendingLabel="Recording…"
      isPending={isSubmitting}
      submitDisabled={!isAmountValid || exceedsOutstanding || !isTimeValid}
      onSubmit={handleSubmit}
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="record-payment-amount">Amount</Label>
          <CurrencyInput
            id="record-payment-amount"
            currency={currency}
            value={amount}
            onChange={setAmount}
          />
          {!isAmountValid && (
            <Typography variant="body-sm" color="destructive">
              Enter an amount greater than 0.
            </Typography>
          )}
          {exceedsOutstanding && (
            <Typography variant="body-sm" color="destructive">
              This exceeds the outstanding balance of{' '}
              {formatCurrency(outstandingAmount, currency)}.
            </Typography>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="record-payment-date">Date paid</Label>
            <DatePicker
              id="record-payment-date"
              value={paidAt}
              onChange={setPaidAt}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="record-payment-time">Time paid</Label>
            <Input
              id="record-payment-time"
              type="time"
              value={time}
              onChange={(changeEvent) => setTime(changeEvent.target.value)}
              required
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="record-payment-note">Note (optional)</Label>
          <Textarea
            id="record-payment-note"
            value={note}
            onChange={(changeEvent) => setNote(changeEvent.target.value)}
            rows={2}
            placeholder="e.g. Bank transfer ref #12345"
          />
        </div>

        <div className="space-y-1.5">
          <Label>Receipt (optional)</Label>
          {file ? (
            <div className="bg-muted flex items-center gap-2 rounded-md px-3 py-2">
              <FileText className="text-muted-foreground size-4 shrink-0" />
              <Typography variant="body-sm" className="min-w-0 flex-1 truncate">
                {file.name}
              </Typography>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setFile(null)}
              >
                <X className="size-4" />
              </Button>
            </div>
          ) : (
            <CsvDropZone
              onFile={handleFile}
              accept=".jpg,.jpeg,.png,.webp,.pdf"
              label="Drag and drop a receipt here, or click to browse"
              hint="Accepted formats: JPEG, PNG, WEBP, PDF · max 10 MB"
              ariaLabel="Receipt drop zone"
            />
          )}
        </div>
      </div>
    </FormDialog>
  )
}
